import { useEffect, useState } from "react";
import {
  applyTheme,
  readThemePreference,
  saveThemePreference,
  type ThemePreference,
} from "../lib/theme";

type IconProps = { type: ThemePreference };

function ThemeIcon({ type }: IconProps) {
  if (type === "light") {
    return (
      <svg aria-hidden="true" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="3.5" />
        <path d="M12 2v2.25M12 19.75V22M2 12h2.25M19.75 12H22M4.93 4.93l1.59 1.59M17.48 17.48l1.59 1.59M19.07 4.93l-1.59 1.59M6.52 17.48l-1.59 1.59" />
      </svg>
    );
  }
  if (type === "dark") {
    return (
      <svg aria-hidden="true" viewBox="0 0 24 24">
        <path d="M20.2 15.1A8.8 8.8 0 0 1 8.9 3.8 8.8 8.8 0 1 0 20.2 15.1Z" />
      </svg>
    );
  }
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="8" />
      <path d="M12 4a8 8 0 0 1 0 16Z" className="theme-icon-fill" />
    </svg>
  );
}

const options: Array<{ value: ThemePreference; label: string; description: string }> = [
  { value: "auto", label: "Auto", description: "Follow this device" },
  { value: "light", label: "Light", description: "Use light appearance" },
  { value: "dark", label: "Dark", description: "Use dark appearance" },
];

export function ThemeSwitcher() {
  const [preference, setPreference] = useState<ThemePreference>(() => readThemePreference());

  useEffect(() => {
    applyTheme(preference);
    if (preference !== "auto") return;
    const systemTheme = window.matchMedia("(prefers-color-scheme: dark)");
    const followSystem = () => applyTheme("auto");
    systemTheme.addEventListener?.("change", followSystem);
    return () => systemTheme.removeEventListener?.("change", followSystem);
  }, [preference]);

  return (
    <div className="theme-switcher" role="group" aria-label="Appearance">
      {options.map((option) => (
        <button
          className="theme-choice"
          data-selected={preference === option.value}
          type="button"
          key={option.value}
          aria-label={`${option.label} theme. ${option.description}.`}
          aria-pressed={preference === option.value}
          title={`${option.label} · ${option.description}`}
          onClick={() => {
            saveThemePreference(option.value);
            setPreference(option.value);
          }}
        >
          <ThemeIcon type={option.value} />
        </button>
      ))}
      <span className="sr-only" role="status" aria-live="polite">Appearance: {preference}</span>
    </div>
  );
}
